import { useAppSelector } from "index";
import { useMemo } from "react";

import { removeDuplicates } from "@utils/array";
import NoDataComponent from "containers/Components/NoDataComponent";
import { useProviderFiles } from "containers/hooks/useData";
import { getProvider } from "fileParsing/definitions";
import { Category, FileCategory, ProviderFile } from "fileParsing/types";
import { AlgoStep } from "fileParsing/wrangler/types";
import Treemap from "./treemap";
import { FilterType } from "@store/filters/types";
import { OptionKey } from "@store/options/types";

export type FileSettings = {
  width: number;
  height: number;
  margin: number;

  size: string;
  color: "category" | "provider" | "both" | "fileType" | "ext" | "nodes" | "random";
  filesize: boolean;

  groupingSize: boolean;
  private: boolean;
  tooltipWidth: number;
  showImages: boolean;
  showFolderHierarchy: boolean;
};

const defaultSettings: FileSettings = {
  width: 1300,
  height: 800,
  size: "uncompressed",
  color: "both",
  filesize: true,
  groupingSize: false,
  private: false,
  tooltipWidth: 400,
  showImages: false,
  margin: 0,
  showFolderHierarchy: true,
};

export type FilePiece = ProviderFile & {
  val: number;
  category: string;
  files: string[];
  sum: boolean;
  datacategories: [string, number][];
  datalength: number;
  relPath: string;
  id: number;
};

export type Selection = (name: string, action: string, value: unknown) => void;

type ComponentProps = {
  settings: Partial<FileSettings>;
};

export const getFileSize = (num: number): string => {
  if (num < 1024) return Math.floor(num) + " Bytes";
  num = num / 1024;
  if (num < 1024) return Math.floor(num * 10) / 10 + " KB";
  num = num / 1024;
  if (num < 1024) return Math.floor(num * 10) / 10 + " MB";
  num = num / 1024;
  return Math.floor(num * 10) / 10 + " GB";
};

function getColorScheme(activeFilter: FilterType[]): FileSettings["color"] {
  // [FilterType.categories, FilterType.fileTypes]
  if (activeFilter.includes(FilterType.categories) && activeFilter.includes(FilterType.fileTypes)) return "both";
  else if (activeFilter.includes(FilterType.categories)) return "category";
  else if (activeFilter.includes(FilterType.fileTypes)) return "fileType";
  return "provider";
}

function FileView(props: ComponentProps) {
  const providerFiles = useProviderFiles();
  const activeFilter = useAppSelector((s) => s.filters.active);
  const fileFilter = useAppSelector((s) => s.filters.fileTypes);
  const catFilter = useAppSelector((s) => s.filters[FilterType.categories]);
  const grouping = useAppSelector((s) => s.options.options[OptionKey.Grouping]);
  const showFolderHierarchy = useAppSelector((s) => s.options.options[OptionKey.ShowFolderHierarchy]);
  const searchString = useAppSelector((s) => s.options.searchString);
  const showImages = useAppSelector((s) => s.options.options[OptionKey.ShowImages]);
  const elementSize = useAppSelector((s) => s.options.options[OptionKey.ElementSize]);
  const settings: Partial<FileSettings> = {
    ...defaultSettings,
    ...{
      showImages: showImages.value,
      showFolderHierarchy: showFolderHierarchy.value,
      groupingSize: grouping.value,
      size: elementSize.value,
      color: getColorScheme(activeFilter),
    },
    ...props.settings,
  };

  /**
   * Supposed size in the treemap based on applied settings and
   * filters.
   * @param f File element to calculate the size from
   * @returns Numeric size value
   */
  const getValue = (f: ProviderFile): number => {
    if (f.isDir) return 0;
    switch (settings.size) {
      case "length":
        return f.data.length;
      case "min":
        return Math.max(Math.min(f.data.length, 1000), 10);
      case "uncompressed":
        return f.uncompressedSize;
      case "log":
        return f.data.length === 0 ? 0 : Math.log(f.data.length);
      case "compressed":
        return f.compressedSize;
      case "evenly":
      default:
        return 1;
    }
  };

  /**
   * Extends providerfile in order to create FilePiece.
   * @param f ProviderFile
   * @param id id of the ProviderFile/FilePiece
   * @returns FilePiece
   */
  const makeFilePiece = (f: ProviderFile, id: number): FilePiece => ({
    ...f,
    id,
    val: getValue(f),
    category: f.fileType,
    files: [f.groupBy === "folder" ? f.folder : f.fileName],
    sum: false,
    datacategories: [f.data[0] && [f.data[0].subcategory, f.data.length]], // TODO: Expect files to only have one data category
    relPath: f.path,
    datalength: f.data.length,
  });

  /**
   * Returns array of FilePieces to display in treemap.
   * Applies filters to FilePieces.
   */
  const data = useMemo(() => {
    const activeFiles = fileFilter.filter((d) => d.value).map((d) => d.id);
    const activeCategories = catFilter.filter((d) => d.value).map((d) => d.id);
    const search = searchString.toLowerCase();

    return providerFiles
      .map((d) => ({
        ...d,
        /* path: {ZIP-filename}/{d.path without d.filename} */
        path: `${d.zipName}/${d.path.substring(0, d.path.length - (d.fileName.length + 1))}`,
      }))
      .filter((d) => activeFiles.includes(d.fileType) && activeCategories.includes(d.dataCategory))
      .map<FilePiece>((f, id) => makeFilePiece(f, id))
      .filter((f) => f.val > 0)
      .filter((f) => {
        return (
          f.fileName.toLowerCase().includes(search) ||
          f.folder.toLowerCase().includes(search) ||
          f.path.toLowerCase().includes(search) ||
          f.relPath.toLowerCase().includes(search)
        );
      });
  }, [providerFiles, fileFilter, settings]);

  /**
   * List of FilePieces to render in a flat treemap.
   */
  const fileList = useMemo(() => {
    const con: FilePiece[] = [];

    const max = data.reduce((prev, cur) => (prev > cur.val ? prev : cur.val), 0);
    const minElemSize = Math.max(0, max * (settings.groupingSize ? 0.02 : 0.0));

    for (const element of data) {
      if (element.val < minElemSize) {
        const groupIx = con.findIndex(
          (_, j) =>
            (element.groupBy === "root" &&
              con[j].providerId === element.providerId &&
              con[j].category === element.category &&
              con[j].sum) ||
            (element.groupBy === "folder" &&
              con[j].folder === element.folder &&
              con[j].fileType === element.fileType &&
              con[j].sum),
        );

        if (groupIx > -1) {
          // Add to existing group
          con[groupIx].val += element.val;
          con[groupIx].files.push(element.path);
        } else {
          // Create new group
          con.push({ ...element, sum: true });
        }
      } else {
        con.push(element);
      }
    }
    return con;
  }, [settings.groupingSize, data]);
  /**
   * List of FilePieces to render in an hierarchical treemap.
   * Adds a "treemap" root node which ought not be rendered an
   * a FilePiece for each directory found.
   */
  const hierarchyData = useMemo(() => {
    const dirs = removeDuplicates(
      data.reduce(
        (arr, f) => {
          const p = f.path.split("/");
          return [
            ...arr,
            ...[getProvider(f.providerId).name, ...p].reduce<[string, string][]>(
              (path, el: string) => [
                ...path,
                [getProvider(f.providerId).name, `${path[path.length - 1]?.[1] ?? "treemap"}/${el}`],
              ],
              [],
            ),
          ];
        },
        [["", "treemap"]],
      ),
    ).map((path, id) =>
      makeFilePiece(
        {
          id,
          exportId: 0,
          providerId: path[0],
          compressedSize: 0,
          fileName: path[1].substring(path[1].lastIndexOf("/") + 1),
          zipName: "",
          fileType: FileCategory.Other,
          dataCategory: Category.Other,
          folder: "",
          hash: "",
          isDir: true,
          path: path[1],
          data: [],
          uncompressedSize: 0,
          groupBy: "",
          date: new Date(),
          binaryData: null,
          mimeType: "unknown/unknown",
          parserData: {
            structure: undefined,
            byteData: undefined,
            algoStep: AlgoStep.ExtractData,
          },
        },
        id,
      ),
    );

    const res = [
      ...dirs,
      ...data.map((f) => ({
        ...f,
        path: `treemap/${getProvider(f.providerId).name}/${f.path}/${f.fileName}`,
      })),
    ];
    return res;
  }, [data]);

  if (data.length === 0) return <NoDataComponent />;

  return <Treemap settings={settings} hierarchyData={hierarchyData} fileList={fileList} />;
}

export default FileView;
